import React from 'react';
import { Video } from '../../types/Video';
import './VideoCard.css';

/// 動画カードのプロパティ
interface VideoCardProps {
  video: Video;
  showStats?: boolean;
}

/// 動画カードコンポーネント - 個々の動画を表示
const VideoCard: React.FC<VideoCardProps> = ({ video, showStats = true }) => {
  /// 視聴回数をフォーマット
  const formatViewCount = (count: number): string => {
    if (count >= 10000) {
      return `${Math.floor(count / 10000)}万`;
    }
    return count.toLocaleString();
  };

  /// 公開日をフォーマット
  const formatPublishedDate = (dateString: string): string => {
    try {
      // 日付部分（YYYY-MM-DD）を抽出してそのまま使用
      // タイムゾーン変換を避けるため、Date オブジェクトを経由せず文字列操作で対応
      const datePart = dateString.substring(0, 10); // "YYYY-MM-DD"
      const [year, month, day] = datePart.split('-').map(Number);
      
      return `${year}年${month}月${day}日`;
    } catch (e) {
      console.error("Date formatting error:", e);
      return '日付不明';
    }
  };

  /// タイトルを短縮
  const truncateTitle = (title: string, maxLength: number = 60): string => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  // メン限タグが含まれているかチェック
  const isRestrictedContent = (): boolean => {
    return video.tags.some(tag => tag.includes('メン限'));
  };

  return (
    <div className="video-card">
      <div className="video-thumbnail">
        <img 
          src={video.thumbnailPath ? `/assets/images/thumbnails/${video.thumbnailId}.jpg` : '/assets/images/icon/not_found.jpg'}
          alt={video.title}
          className="thumbnail-image"
          onError={(e) => {
            // サムネイル画像が見つからない場合のフォールバック
            e.currentTarget.src = '/assets/images/icon/not_found.jpg';
          }}
        />
        <div className="video-duration">{video.duration}</div>
        {!isRestrictedContent() ? (
          <a 
            href={video.videoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="video-overlay"
            aria-label={`${video.title}を再生`}
          >
            <div className="play-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
              </svg>
            </div>
          </a>
        ) : (
          <div className="video-overlay">
            <div className="play-button restricted" aria-label="メンバー限定コンテンツ">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 7C13.1 7 14 7.9 14 9S13.1 11 12 11 10 10.1 10 9 10.9 7 12 7ZM12 17C10.7 17 9.56 16.5 8.73 15.67C8.93 14.85 10.6 14.4 12 14.4S15.07 14.85 15.27 15.67C14.44 16.5 13.3 17 12 17Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
        )}
      </div>
      
      <div className="video-info">
        <h3 className="video-title" title={video.title}>
          {truncateTitle(video.title)}
        </h3>
        
        {showStats && (
          <div className="video-stats">
            <span className="view-count">
              {formatViewCount(video.viewCount)}回再生
            </span>
            <span className="like-count">
              ❤️{video.likeCount}
            </span>
            <span className="published-date">
              {formatPublishedDate(video.publishedAt)}
            </span>
          </div>
        )}
        
        {video.tags.length > 0 && (
          <div className="video-tags">
            {video.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="video-tag">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoCard; 